import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslate } from "helpers";
import { Errors } from "services/forms/validation/types";
import { FleetTireSetEditFormValues } from "services/api/fleet/tires/types";
import { initialFleetTireSetEditForm } from "services/api/fleet/tires/consts";
import { getFleetTireSetAPI, relocateFleetTireSetAPI } from "services/api/fleet/tires/api";
import { transformFleetTireSetEditForm } from "services/api/fleet/tires/transformations";
import { getFleetWarehouseAPI } from "services/api/fleet/warehouses/api";
import { Modal, SuccessOverlay } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import WizardStep from "ui/components/Wizard/WizardStep";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import { Loader } from "ui/components/Loader";
import successAnimation from "ui/theme/animations/success.json";
import { TireSetRelocateEditCardProps, RelocateFormValues } from "./types";
import RelocateStep1 from "./components/RelocateStep1";
import RelocateSummary from "./components/RelocateSummary";
import TireSetRelocateIntro from "./components/TireSetRelocateIntro/TireSetRelocateIntro";

const initialRelocateFormValues: RelocateFormValues = {
  targetWarehouseId: null,
  transferDate: new Date(),
};

interface ResolvedRelocateNames {
  targetWarehouse?: { name?: string | null; address?: string | null };
}

const TireSetRelocateEditCard: React.FC<TireSetRelocateEditCardProps> = (props) => {
  const { show, tireSetId, onSuccess, onClose } = props;
  const { t } = useTranslate();

  const [stepCurrent, setStepCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [currentSet, setCurrentSet] = useState<FleetTireSetEditFormValues>(initialFleetTireSetEditForm);
  const [formValues, setFormValues] = useState<RelocateFormValues>(initialRelocateFormValues);
  const [errors, setErrors] = useState<Errors>({});
  const [resolvedNames, setResolvedNames] = useState<ResolvedRelocateNames>({});

  useEffect(() => {
    if (show && tireSetId) {
      setStepCurrent(0);
      setFormValues(initialRelocateFormValues);
      setErrors({});
      fetchTireSet(tireSetId);
    }
  }, [show, tireSetId]);

  const fetchTireSet = async (id: string) => {
    setIsLoading(true);
    try {
      const result = await getFleetTireSetAPI(id);
      if (result) {
        const setForm = transformFleetTireSetEditForm(result);
        setCurrentSet(setForm);
      }
    } catch { /* */ }
    setIsLoading(false);
  };

  const handleChange = useCallback((partial: Partial<RelocateFormValues>) => {
    setFormValues((prev) => ({ ...prev, ...partial }));
    setErrors({});
  }, []);

  const validateStep = (): boolean => {
    const newErrors: Errors = {};

    if (stepCurrent === 0) {
      if (!formValues.targetWarehouseId) {
        newErrors.targetWarehouseId = "Odaberite skladište";
      }
      if (!formValues.transferDate) {
        newErrors.transferDate = "Odaberite datum premještaja";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resolveNamesForSummary = async () => {
    const names: ResolvedRelocateNames = {};
    try {
      if (formValues.targetWarehouseId) {
        const w = await getFleetWarehouseAPI(formValues.targetWarehouseId);
        names.targetWarehouse = {
          name: w.name,
          address: [w.addressLine1, w.addressPostalCode, w.addressCity, w.addressCountry].filter(Boolean).join(", "),
        };
      }
    } catch { /* */ }
    setResolvedNames(names);
  };

  const handleNextStep = async () => {
    if (!validateStep()) return;

    const nextStep = stepCurrent + 1;

    // Resolve nazive prije ulaska u summary korak
    if (nextStep === 1) {
      await resolveNamesForSummary();
    }

    setStepCurrent(nextStep);
  };

  const handleBackStep = () => {
    setStepCurrent((s) => s - 1);
  };

  const handleFinish = async () => {
    setIsLoading(true);
    try {
      await relocateFleetTireSetAPI(tireSetId, {
        targetWarehouseId: formValues.targetWarehouseId!,
        transferDate: formValues.transferDate,
      }, t);

      setIsLoading(false);
      setShowSuccessOverlay(true);
    } catch {
      setIsLoading(false);
    }
  };

  const handleSuccessDismiss = () => {
    setShowSuccessOverlay(false);
    setFormValues(initialRelocateFormValues);
    setStepCurrent(0);
    if (onSuccess) onSuccess();
  };

  const steps = useMemo(() => [
    <WizardStep key={1} stepNumber={0} stepCurrent={stepCurrent}>
      <RelocateStep1
        currentSet={currentSet}
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
      />
    </WizardStep>,
    <WizardStep key={2} stepNumber={1} stepCurrent={stepCurrent}>
      <RelocateSummary
        currentSet={currentSet}
        formValues={formValues}
        resolvedNames={resolvedNames}
      />
    </WizardStep>,
  ], [stepCurrent, currentSet, formValues, errors, handleChange, resolvedNames]);

  return (
    <>
      <SuccessOverlay
        show={showSuccessOverlay}
        animation={successAnimation}
        title="Premještaj je uspješno izvršen!"
        description="Set guma je premješten na novo skladište."
        onDismiss={handleSuccessDismiss}
      />
      <Modal
        show={show}
        onHide={onClose ?? (() => {})}
        position="side"
        size="full"
        intro={{
          content: (
            <TireSetRelocateIntro
              currentSet={currentSet}
              formValues={formValues}
              targetWarehouseName={resolvedNames.targetWarehouse?.name}
              stepCurrent={stepCurrent}
              stepCount={steps.length}
            />
          ),
        }}
      >
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title="Premještaj seta guma"
          onHide={onClose ?? (() => {})}
        />
        <ModalBody>
          {isLoading && (
            <div className="relative h-[calc(100vh-80px)]">
              <Loader isLoading={true}>{null}</Loader>
            </div>
          )}
          <div className={isLoading ? "hidden" : ""}>
            <Wizard steps={steps} />
          </div>
        </ModalBody>
        <WizardFooter
          stepCurrent={stepCurrent}
          steps={steps}
          onNextStep={handleNextStep}
          onBackStep={handleBackStep}
          onFinish={handleFinish}
          isLoading={isLoading}
          isReadOnly={false}
        />
      </Modal>
    </>
  );
};

export default memo(TireSetRelocateEditCard);
